import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  MenuItem,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Avatar,
  useMediaQuery,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import GridOnIcon from "@mui/icons-material/GridOn";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BRANCH_URL = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;

const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const lightStyles = {
  container: { minHeight: "100vh", bgcolor: "#ffffff", py: 4 },
  paper: {
    bgcolor: "#f8f9fa",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#dddddd" },
      "&:hover fieldset": { borderColor: "#ffb347" },
      "&.Mui-focused fieldset": { borderColor: "#ffb347" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
    "& .MuiSelect-icon": { color: "#000000" },
  },
  tableHeader: {
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #ffb347",
    backgroundColor: "#f1f3f4",
  },
  tableCell: { color: "#000000", borderBottom: "1px solid #e0e0e0" },
  actionBtn: {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 0 15px #FFD600",
    },
  },
};

// Custom scrollbar styles
const scrollbarStyles = `
  *::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  *::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  *::-webkit-scrollbar-thumb {
    background: #ffb347;
    border-radius: 10px;
  }
  *::-webkit-scrollbar-thumb:hover {
    background: #ffa01c;
  }
  * {
    scrollbar-width: thin;
    scrollbar-color: #ffb347 #f1f1f1;
  }
`;

const BranchEvents = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchSearch, setBranchSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Faulty");
  const [shiftType, setShiftType] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  //for table2
  const [branches2, setBranches2] = useState([]);
  const [branchSearch2, setBranchSearch2] = useState("");
  const [statusFilter2, setStatusFilter2] = useState("All");
  const [page2, setPage2] = useState(0);
  const [rowsPerPage2, setRowsPerPage2] = useState(50);

  //table2
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, message: msg, severity });

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BRANCH_URL}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allBranches = Array.isArray(res.data) ? res.data : [];
      setBranches(allBranches);
      setBranches2(allBranches);
    } catch (err) {
      showSnackbar("Registry Sync Failed", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchLastUpdate = useCallback(async () => {
    try {
      const res = await axios.get(`${BRANCH_URL}/lastUpdate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastUpdate(res.data.lastUpdate || "—");
      setShiftType(res.data.shiftType || "Normal");
      setServerStatus(res.data.serverStatus || "Unknown");
    } catch (err) {
      showSnackbar("Registry Sync Failed", "error");
    }
  }, [token]);

  useEffect(() => {
    fetchBranches();
    fetchLastUpdate();
    const intervalId = setInterval(() => {
      const minute = new Date().getMinutes();
      if (minute % 4 === 3) {
        fetchBranches();
        fetchLastUpdate();
      }
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchBranches, fetchLastUpdate]);

  useEffect(() => {
    fetchLastUpdate();
    const intervalId = setInterval(() => {
      fetchLastUpdate();
    }, 1 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchLastUpdate]);

  const filteredBranches = useMemo(() => {
    let eventsOnly = branches.filter(
      (b) => b.eventState === "recent" && b.duration > 7,
    );
    eventsOnly.sort((a, b) => new Date(b.eventTime) - new Date(a.eventTime));
    let filtered = eventsOnly.filter((b) =>
      Object.values(b)
        .join(" ")
        .toLowerCase()
        .includes(branchSearch.toLowerCase()),
    );
    if (!statusFilter || statusFilter === "All") return filtered;
    if (statusFilter === "Active")
      return filtered.filter((b) => b.status === "Active");
    if (statusFilter === "Faulty")
      return filtered.filter(
        (b) => b.status.includes("Down") || b.status === "Int",
      );
    return filtered;
  }, [branches, branchSearch, statusFilter]);

  const filteredBranches2 = useMemo(() => {
    let downBranches = branches2.filter(
      (b) => b.status.includes("Down") || b.status.includes("Int"),
    );
    let filtered = downBranches.filter((b) =>
      Object.values(b)
        .join(" ")
        .toLowerCase()
        .includes(branchSearch2.toLowerCase()),
    );
    if (!statusFilter2 || statusFilter2 === "All") filtered = filtered;
    else if (statusFilter2 === "Requiring")
      filtered = filtered.filter((b) => b.action === "Requiring");
    else if (statusFilter2 === "Pending")
      filtered = filtered.filter((b) => b.action === "Pending");
    filtered.sort((a, b) => {
      const tA = a.eventTime ? new Date(a.eventTime).getTime() : 0;
      const tB = b.eventTime ? new Date(b.eventTime).getTime() : 0;
      return tA - tB;
    });
    return filtered;
  }, [branches2, branchSearch2, statusFilter2]);

  const paginatedBranches = useMemo(
    () =>
      filteredBranches.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredBranches, page, rowsPerPage],
  );
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedBranches2 = useMemo(
    () =>
      filteredBranches2.slice(
        page2 * rowsPerPage2,
        page2 * rowsPerPage2 + rowsPerPage2,
      ),
    [filteredBranches2, page2, rowsPerPage2],
  );

  const handleChangePage2 = (event, newPage) => setPage2(newPage);
  const handleChangeRowsPerPage2 = (event) => {
    setRowsPerPage2(parseInt(event.target.value, 10));
    setPage2(0);
  };

  function formatThis(dateValue) {
    if (!dateValue) return "—";
    const d = new Date(dateValue);
    if (isNaN(d)) return "—";
    return d
      .toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  }

  function getmins(from, to) {
    if (!from || !to) return 0;
    const ms = new Date(to) - new Date(from);
    if (ms < 0) return 0;
    const minutes = Math.floor(ms / (1000 * 60));
    return minutes;
  }

  function getDuration(from, to) {
    if (!from || !to) return "0m";
    const ms = new Date(to) - new Date(from);
    if (ms <= 0) return "0m";
    let totalMinutes = Math.floor(ms / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    totalMinutes %= 60 * 24;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;
    return result.trim();
  }

  function formatDuration(totalMinutes) {
    const days = Math.floor(totalMinutes / (24 * 60));
    totalMinutes %= 24 * 60;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;

    return result.trim() || "0m";
  }

  const lightPaper = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
  };

  // Calculate table height for mobile
  const toolbarHeight = 60; // or get via ref.offsetHeight
  const paginationHeight = 80;
  const headerHeight = 80;

  // Calculate table height based on screen size
  const getTableHeight = () => {
    const availableHeight =
      window.innerHeight - (toolbarHeight + paginationHeight + headerHeight);
    return `${availableHeight}px`;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 }}>
      <style>{scrollbarStyles}</style>

      {(serverStatus === "Down" ||
        serverStatus === "Unknown" ||
        shiftType === "Sunday" ||
        shiftType === "Night") && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            px: 10,
            py: 5,
            borderRadius: "20px",
            bgcolor: "rgba(255,255,255,0.95)",

            border:
              serverStatus === "Down"
                ? "3px solid red"
                : shiftType === "Night"
                  ? "3px solid black"
                  : "3px solid orange",

            animation:
              serverStatus === "Down"
                ? "blinkRed 1s infinite, glowRed 1.5s infinite alternate"
                : "blinkOrange 1.2s infinite",
          }}
        >
          <Typography
            sx={{
              color:
                serverStatus === "Down"
                  ? "red"
                  : shiftType === "Night"
                    ? "black"
                    : "orange",

              fontWeight: "bold",
              fontSize: "3.6rem",
              textAlign: "center",
              letterSpacing: "4px",
            }}
          >
            {serverStatus === "Down"
              ? "🚩 NETWORK ERROR"
              : serverStatus === "Unknown"
                ? "⚠️ SERVER IP NOT SET"
                : shiftType === "Sunday"
                  ? "🟡 SUNDAY : MODE"
                  : shiftType === "Night"
                    ? "🌙 NIGHT : MODE"
                    : ""}
          </Typography>

          <style>
            {`
        @keyframes blinkRed {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }

        @keyframes glowRed {
          from { box-shadow: 0 0 15px red; }
          to { box-shadow: 0 0 50px red, 0 0 90px darkred; }
        }

        @keyframes blinkOrange {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}
          </style>
        </Box>
      )}

      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, md: 0.5 },
        }}
      >
        {/* ------------------- TABLES ------------------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
          }}
        >
          {/* ------------------- TABLE 1 ------------------- */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: isMobile ? "450px" : "auto",
              width: "100%",
            }}
          >
            {/* Controls */}
            <Paper
              sx={{
                p: 1.5,
                mb: 1,
                ...lightStyles.paper,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* LEFT - FORCE SINGLE LINE */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "nowrap",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <TextField
                  placeholder="Search..."
                  size="small"
                  value={branchSearch}
                  onChange={(e) => setBranchSearch(e.target.value)}
                  sx={{ ...lightStyles.input, flex: 1 }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    color: "#000000",
                    fontWeight: "bold",
                    border: "1px solid #e0e0e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                    bgcolor: "rgba(255,214,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "36px",
                  }}
                >
                  {filteredBranches.length}
                </Typography>

                <IconButton
                  onClick={() => {
                    fetchBranches();
                    showSnackbar("✅ Table updated", "success");
                  }}
                  sx={{
                    ...yellowButton,
                    background: "transparent",
                    border: "1px solid #FFD600",
                    flexShrink: 0,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* RIGHT */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: "bold",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                    whiteSpace: "nowrap",
                  }}
                >
                  Events
                </Typography>

                <TextField
                  select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ ...lightStyles.input, width: isMobile ? "50%" : 120 }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Faulty">Faulty</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Table with vertical scroll */}
            <TableContainer
              sx={{
                height: getTableHeight,
                maxHeight: getTableHeight,
                overflow: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Table stickyHeader sx={{ minWidth: "600px" }}>
                <TableHead>
                  <TableRow>
                    {["Event", "Name", "Problem", "Actions", "Event Time"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            ...lightStyles.tableHeader,
                            bgcolor: "#f1f3f4",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {h}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBranches.map((b, i) => (
                    <TableRow
                      key={b._id || i}
                      sx={{
                        "&:hover": { bgcolor: "rgba(255, 180, 70, 0.1)" },
                        ...(b.eventTime !== "expired"
                          ? {
                              animation: "blinkRow 1s infinite alternate",
                              bgcolor:
                                b.status === "Active"
                                  ? "#e8f5e9"
                                  : b.status === "Intermittent"
                                    ? "#fff3e0"
                                    : "#ffebee",
                            }
                          : {}),
                      }}
                    >
                      <TableCell
                        sx={{ ...lightStyles.tableCell, textAlign: "center" }}
                      >
                        {b.eventTime !== "expired" && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              mx: "auto",
                              bgcolor:
                                b.status === "Active"
                                  ? "#4caf50"
                                  : b.status === "Intermittent"
                                    ? "#ff9800"
                                    : "#f44336",
                              animation:
                                (b.status === "Down" ||
                                  b.status === "Intermittent") &&
                                b.action === "Requiring" &&
                                b.eventTime !== "expired"
                                  ? "blinkStatus 1s infinite alternate"
                                  : "none",
                              boxShadow:
                                b.status === "Active"
                                  ? "0 0 6px #4caf50"
                                  : b.status === "Intermittent"
                                    ? "0 0 10px #ff9800"
                                    : "0 0 10px #f44336",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={lightStyles.tableCell}>{b.name}</TableCell>
                      <TableCell sx={lightStyles.tableCell}>
                        {b.problem}
                      </TableCell>
                      <TableCell sx={lightStyles.tableCell}>
                        {b.action === "Requiring" ? "Req" : b.action}
                      </TableCell>
                      <TableCell sx={lightStyles.tableCell}>
                        {b.duration ? `${b.duration} mins ago` : "0 mins ago"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[50, 100, 200, 500, 1000, 3000]}
              component="div"
              count={filteredBranches.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ color: "#000000", borderTop: "1px solid #e0e0e0" }}
            />
          </Box>

          {/* ------------------- TABLE 2 ------------------- */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: isMobile ? "450px" : "auto",
              width: "100%",
            }}
          >
            {/* Controls */}
            <Paper
              sx={{
                p: 1.5,
                mb: 1,
                ...lightStyles.paper,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* LEFT - FORCE SINGLE LINE */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "nowrap",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <TextField
                  placeholder="Search..."
                  size="small"
                  value={branchSearch2}
                  onChange={(e) => setBranchSearch2(e.target.value)}
                  sx={{ ...lightStyles.input, flex: 1 }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    color: "#000000",
                    fontWeight: "bold",
                    border: "1px solid #e0e0e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                    bgcolor: "rgba(255,214,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "36px",
                  }}
                >
                  {filteredBranches2.length}
                </Typography>

                <IconButton
                  onClick={() => {
                    fetchBranches();
                    showSnackbar("✅ Table 2 updated", "success");
                  }}
                  sx={{
                    ...yellowButton,
                    background: "transparent",
                    border: "1px solid #FFD600",
                    flexShrink: 0,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* RIGHT */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <TextField
                  select
                  size="small"
                  value={statusFilter2}
                  onChange={(e) => setStatusFilter2(e.target.value)}
                  sx={{ ...lightStyles.input, width: isMobile ? "100%" : 120 }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Requiring">Requiring</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Table with vertical scroll */}
            <TableContainer
              sx={{
                height: getTableHeight,
                maxHeight: getTableHeight,
                overflow: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Table stickyHeader sx={{ minWidth: "600px" }}>
                <TableHead>
                  <TableRow>
                    {[
                      "Name",
                      "Status",
                      "Problem",
                      "Actions",
                      "Down Time",
                      "Lost time",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          ...lightStyles.tableHeader,
                          bgcolor: "#f1f3f4",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBranches2.map((b, i) => (
                    <TableRow
                      key={b._id || i}
                      sx={{ "&:hover": { bgcolor: "rgba(255, 180, 70, 0.1)" } }}
                    >
                      <TableCell
                        sx={{ ...lightStyles.tableCell, color: "#d32f2f" }}
                      >
                        {b.name}
                      </TableCell>
                      <TableCell
                        sx={{ ...lightStyles.tableCell, color: "#d32f2f" }}
                      >
                        {b.status}
                      </TableCell>
                      <TableCell sx={lightStyles.tableCell}>
                        {b.problem}
                      </TableCell>
                      <TableCell
                        sx={{ ...lightStyles.tableCell, color: "#d32f2f" }}
                      >
                        {b.action === "Requiring" ? "Req" : b.action}
                      </TableCell>
                      <TableCell
                        sx={{ ...lightStyles.tableCell, color: "#d32f2f" }}
                      >
                        {b.eventTime ? formatThis(b.eventTime) : "00:00:00"}
                      </TableCell>
                      <TableCell sx={lightStyles.tableCell}>
                        {b.duration
                          ? `${formatDuration(b.duration)}`
                          : "0 mins"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={filteredBranches2.length}
              rowsPerPage={rowsPerPage2}
              page={page2}
              onPageChange={handleChangePage2}
              onRowsPerPageChange={handleChangeRowsPerPage2}
              sx={{ color: "#000000", borderTop: "1px solid #e0e0e0" }}
            />
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            sx={{
              bgcolor: "#ffffff",
              color: "#000000",
              border: "1px solid #ffb347",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <style>{`
          @keyframes blinkStatus{0%{opacity:1}50%{opacity:0.4}100%{opacity:1}}
          @keyframes blinkRow{0%{opacity:1}50%{opacity:0.6}100%{opacity:1}}
        `}</style>
      </Container>
    </Box>
  );
};

export default BranchEvents;
